import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, IconButton } from '@mui/material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useReports } from './useReports';
import './reportViewer.css';

const extractGroupId = (odataContext) => {
  if (!odataContext) return null;
  const match = odataContext.match(/groups\/([0-9a-fA-F-]{36})/);
  return match ? match[1] : null;
};

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const APP_HEADER_HEIGHT = 100;

const ReportViewer = () => {
  const history = useHistory();
  const { reportId: reportIdParam } = useParams();
  const location = useLocation();
  const state = location.state ?? {};

  const reportID = state.reportID ?? reportIdParam ?? '';
  const varID = state.varID ?? '';
  const reportName = state.reportName ?? 'Report';

  const { getEmbedToken } = useReports();
  const [embedToken, setEmbedToken] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const fetchEmbed = async () => {
      try {
        const res = await getEmbedToken(varID);
        const groupId = extractGroupId(res?.['@odata.context']);
        const token = res?.token;

        if (!token || !groupId) {
          setError('Unable to load report.');
          return;
        }

        setEmbedToken(token);
        setEmbedUrl(`https://app.powerbi.com/reportEmbed?reportId=${varID}&groupId=${groupId}`);
      } catch (err) {
        console.log(err);
        setError('Unable to load report.');
      }
    };
    if (varID) fetchEmbed();
  }, [varID]);

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  if (error) {
    return (
      <Box sx={{ display: 'flex', flex: 1, height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!embedToken || !embedUrl) {
    return (
      <Box sx={{ display: 'flex', flex: 1, height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
        minHeight: 0,
      }}
    >
      {/* Compact header */}
      <Box
        sx={{
          px: 2,
          py: 0.5,
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <IconButton size="small" onClick={() => history.push('/powerBIReports')}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {reportName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={zoomOut} disabled={zoom <= ZOOM_MIN}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton size="small" onClick={zoomIn} disabled={zoom >= ZOOM_MAX}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={zoomReset}>
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Report area: definite height (flex:1 + minHeight:0), no outer scroll */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            minWidth: '100%',
            minHeight: '100%',
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
          }}
        >
          <PowerBIEmbed
            embedConfig={{
              type: 'report',
              id: varID,
              embedUrl,
              accessToken: embedToken,
              tokenType: models.TokenType.Embed,
              permissions: models.Permissions.Read,
              viewMode: models.ViewMode.View,
              settings: {
                layoutType: models.LayoutType.Custom,
                customLayout: {
                  displayOption: models.DisplayOption.FitToPage,
                },
                background: models.BackgroundType.Transparent,
                filterPaneEnabled: false,      // removes the Filters pane on the right
                navContentPaneEnabled: true,   // keeps page tabs (WIP Report / Audit Trial)
              },
            }}
            cssClassName="power-bi-report-container"
            getEmbeddedComponent={(embeddedReport) => {
              const report = embeddedReport;
              report.off('error');
              report.on('error', (event) => {
                console.log(event.detail);
              });
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ReportViewer;


